import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, AlertTriangle, Check } from "lucide-react";
import { ApiError } from "@finora/api-client";
import { Button, EmptyState, Skeleton, StatusBadge } from "@finora/ui";
import { formatMoney } from "@finora/config";
import { api } from "../../lib/api";
import { ModuleGateNotice } from "../../components/ModuleGateNotice";

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function yesterday(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return toIsoDate(d);
}

// Razgovor 23.09.2026: "Preskočeno X grešaka" - ne zna se o čemu se radi.
// Backend PRIVREMENO (dok se ne vidi šta se stvarno dešava u produkciji)
// piše dijagnostiku preskočenih stavki/plaćanja u POSTOJEĆE lastError/
// postingLastError polje, prefiksovanu sa "[DEBUG]" - ovo NIJE prava
// greška slanja (izveštaj je i dalje uspešno poslat/potvrđen), pa se
// prikazuje amber umesto crveno da se ne meša sa stvarnim neuspehom
// (status FAILED, koji i dalje ide crveno).
function errorCellClass(message: string | null): string {
  return message?.startsWith("[DEBUG]") ? "text-amber-700" : "text-red-600";
}

const STATUS_TONE: Record<string, "success" | "warning" | "neutral"> = {
  CONFIRMED: "success",
  SENT: "success",
  PENDING: "warning",
  FAILED: "warning",
  SKIPPED_EMPTY: "neutral",
};
const STATUS_LABEL: Record<string, string> = {
  CONFIRMED: "Potvrđeno",
  SENT: "Poslato",
  PENDING: "U toku",
  FAILED: "Neuspelo",
  SKIPPED_EMPTY: "Nema prometa",
};

export function MinimaxReportsTab() {
  const queryClient = useQueryClient();
  const [subTab, setSubTab] = useState<"promet" | "dnevni">("promet");
  const [date, setDate] = useState(yesterday());

  const {
    data: status,
    isError: isStatusError,
    error: statusError,
  } = useQuery({ queryKey: ["minimax-status"], queryFn: () => api.getMinimaxStatus(), retry: false });
  const { data: reports, isLoading } = useQuery({
    queryKey: ["minimax-reports"],
    queryFn: () => api.listMinimaxReports(),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["minimax-reports"] });
    queryClient.invalidateQueries({ queryKey: ["minimax-status"] });
  };

  const sendStockReport = useMutation({
    mutationFn: () => api.sendMinimaxDailyReport(date),
    onSuccess: invalidate,
  });
  const sendInvoicePosting = useMutation({
    mutationFn: () => api.sendMinimaxDailyInvoicePosting(date),
    onSuccess: invalidate,
  });

  if (isStatusError && statusError instanceof ApiError && statusError.response.errorCode === "MODULE_NOT_AVAILABLE") {
    return <ModuleGateNotice error={statusError} moduleKey="minimax_api" />;
  }

  if (!status?.connected) {
    return (
      <EmptyState
        icon={AlertTriangle}
        title="MiniMax nije povezan"
        description="Poveži MiniMax nalog u Podešavanja → MiniMax da bi video promet i dnevne izveštaje ovde."
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2 rounded-xl border border-border bg-surface p-4 shadow-subtle">
        <div>
          <label className="mb-1 block text-xs text-slate-500">Datum</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-9 rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <Button size="sm" variant="secondary" disabled={sendStockReport.isPending} onClick={() => sendStockReport.mutate()}>
          <RefreshCw className="h-3.5 w-3.5" aria-hidden />
          Sinhronizuj promet
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={sendInvoicePosting.isPending}
          onClick={() => sendInvoicePosting.mutate()}
        >
          <RefreshCw className="h-3.5 w-3.5" aria-hidden />
          Knjiži dnevni izveštaj
        </Button>
      </div>
      {(sendStockReport.isError || sendInvoicePosting.isError) && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {sendStockReport.error instanceof Error
            ? sendStockReport.error.message
            : sendInvoicePosting.error instanceof Error
              ? sendInvoicePosting.error.message
              : "Sinhronizacija nije uspela."}
        </p>
      )}

      <div className="flex gap-1 border-b border-border">
        <button
          onClick={() => setSubTab("promet")}
          className={`border-b-2 px-4 py-2 text-sm font-medium ${
            subTab === "promet" ? "border-brand text-brand" : "border-transparent text-slate-500"
          }`}
        >
          Promet
        </button>
        <button
          onClick={() => setSubTab("dnevni")}
          className={`border-b-2 px-4 py-2 text-sm font-medium ${
            subTab === "dnevni" ? "border-brand text-brand" : "border-transparent text-slate-500"
          }`}
        >
          Dnevni izveštaj
        </button>
      </div>

      <div className="rounded-xl border border-border bg-surface shadow-subtle">
        {isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : !reports || reports.length === 0 ? (
          <div className="p-4">
            <EmptyState icon={AlertTriangle} title="Još nema poslatih izveštaja" />
          </div>
        ) : subTab === "promet" ? (
          <table>
            <thead>
              <tr>
                <th>Datum</th>
                <th>Status</th>
                <th>Artikala</th>
                <th>Preskočeno</th>
                <th>Vrednost prometa</th>
                <th>Greška</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.reportDate}>
                  <td className="font-medium text-slate-800">
                    {new Date(r.reportDate).toLocaleDateString("sr-RS")}
                  </td>
                  <td>
                    <StatusBadge label={STATUS_LABEL[r.status] ?? r.status} tone={STATUS_TONE[r.status] ?? "neutral"} />
                  </td>
                  <td>{r.itemCount}</td>
                  <td>{r.skippedItemCount}</td>
                  <td className="font-medium text-slate-700">{formatMoney(r.totalValue)}</td>
                  <td className={errorCellClass(r.lastError)}>{r.lastError ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Datum</th>
                <th>Status knjiženja</th>
                <th>Dnevni promet (bruto)</th>
                <th>Greška</th>
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.reportDate}>
                  <td className="font-medium text-slate-800">
                    {new Date(r.reportDate).toLocaleDateString("sr-RS")}
                  </td>
                  <td>
                    {r.postingStatus ? (
                      <StatusBadge
                        label={STATUS_LABEL[r.postingStatus] ?? r.postingStatus}
                        tone={STATUS_TONE[r.postingStatus] ?? "neutral"}
                      />
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                  <td className="font-medium text-slate-700">
                    {r.postingStatus ? formatMoney(r.postingTotalAmount) : "—"}
                  </td>
                  <td className={r.postingLastError ? errorCellClass(r.postingLastError) : "text-green-600"}>
                    {r.postingLastError ?? <Check className="h-4 w-4" aria-label="Bez grešaka" />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
